import {
  Camera,
  Constants,
  DynamicTexture,
  Effect,
  EngineStore,
  GeometryBufferRenderer,
  type Nullable,
  PassPostProcess,
  PostProcess,
  PostProcessRenderEffect,
  PrePassRenderer,
  Scene,
  Texture,
  TmpVectors,
  Vector3,
} from '@babylonjs/core';

import { SSAOOptions } from './PostProcess';
import { registerSSAOShader } from './shaders/SSAOShader';

import type { PrePassEffectConfiguration } from '@babylonjs/core/Rendering/prePassEffectConfiguration';

export class SSAOEffect extends PostProcessRenderEffect {
  private static readonly ORTHO_DEPTH_PROJECTION = [1, 0, 0, 0, 1, 0, 0, 0, 1];
  private static readonly PERSPECTIVE_DEPTH_PROJECTION = [0, 0, 0, 0, 0, 0, 1, 1, 1];
  public static get IsSupported(): boolean {
    const engine = EngineStore.LastCreatedEngine;
    if (!engine) {
      return false;
    }
    return engine._features.supportSSAO2;
  }

  private scene: Scene;
  private options: SSAOOptions;

  private pipelineTextureType: number;

  // Post Process
  private _effects: Array<PostProcess> = [];
  public get effects() {
    return this._effects;
  }
  private originalColorPostProcess!: PassPostProcess;
  private ssaoPostProcess!: PostProcess;
  private blurHPostProcess!: PostProcess;
  private blurVPostProcess!: PostProcess;
  private ssaoCombinePostProcess: PostProcess;

  // MSAA Texture Sample
  private _textureSamples: number = 1;
  public set textureSamples(n: number) {
    this._textureSamples = n;

    if (this.prePassRenderer) {
      // prePassRenderer 사용 시 SSAO, MSAA 충돌 버그 있음
      // this._prePassRenderer.samples = n;
    } else {
      this.originalColorPostProcess.samples = n;
    }
  }
  public get textureSamples(): number {
    return this._textureSamples;
  }

  // Renderer
  private forceGeometryBuffer: boolean = false;
  private get geometryBufferRenderer(): Nullable<GeometryBufferRenderer> {
    if (!this.forceGeometryBuffer) {
      return null;
    }
    return this.scene.geometryBufferRenderer;
  }
  private get prePassRenderer(): PrePassRenderer {
    return this.scene.prePassRenderer!;
  }

  // ETC
  private sampleSphere: number[] = [];
  private randomTexture!: DynamicTexture;

  constructor(
    scene: Scene,
    options: SSAOOptions,
    pipelineTextureType = 0,
    forceGeometryBuffer = true,
  ) {
    registerSSAOShader();

    super(
      scene.getEngine(),
      'ssao',
      () => {
        return this._effects;
      },
      true
    );

    this.scene = scene;
    this.pipelineTextureType = pipelineTextureType;
    this.forceGeometryBuffer = forceGeometryBuffer;

    if (this.forceGeometryBuffer) {
      const gBuffer = scene.enableGeometryBufferRenderer(0.5);
      if (gBuffer) {
        gBuffer.enablePosition = false;
        gBuffer.enableReflectivity = false;
        gBuffer.enableVelocity = false;
      }
      if (this.scene.autoClearDepthAndStencil) this.scene.autoClearDepthAndStencil = false;
    } else {
      scene.enablePrePassRenderer();
      this.scene.autoClearDepthAndStencil = true;
    }

    this.options = options;
    const ssaoRatio = this.options.ssaoRatio || 1;
    const blurRatio = this.options.blurRatio || 1;

    this.createRandomTexture();

    this.originalColorPostProcess = this.createOriginalColorPostProcess(this.pipelineTextureType);

    this.ssaoPostProcess = this.createSSAOPostProcess(ssaoRatio, this.pipelineTextureType);

    const blurPostProcess = this.createBlurPostProcess(ssaoRatio, blurRatio, this.pipelineTextureType);
    this.blurHPostProcess = blurPostProcess.H;
    this.blurVPostProcess = blurPostProcess.V;

    this.ssaoCombinePostProcess = this.createSSAOCombinePostProcess(this.pipelineTextureType);

    this._effects = [
      this.originalColorPostProcess,
      this.ssaoPostProcess,
      this.blurHPostProcess,
      this.blurVPostProcess,
      this.ssaoCombinePostProcess,
    ];
  }

  // --------------------------------
  // Common
  // --------------------------------
  public disposeEffects(camera: Camera) {
    for (let effectIndex = 0; effectIndex < this._effects.length; effectIndex++) {
      this._effects[effectIndex]?.dispose(camera);
    }
  }
  public dispose() {
    if (this.randomTexture) this.randomTexture.dispose();
  }
  public updateEffects() {
    this.originalColorPostProcess.updateEffect();
    this.ssaoPostProcess.updateEffect(this.getDefinesForSSAO());

    const blurDefine = this.getDefinesForBlur(this.options.expensiveBlur, this.options.bypassBlur);
    this.blurHPostProcess.updateEffect(blurDefine.h);
    this.blurVPostProcess.updateEffect(blurDefine.v);

    this.ssaoCombinePostProcess.updateEffect();
  }
  public isReady() {
    for (let effectIndex = 0; effectIndex < this._effects.length; effectIndex++) {
      if (!this._effects[effectIndex]?.isReady()) {
        return false;
      }
    }
    return true;
  }

  public updateOptions(options: Partial<SSAOOptions>) {
    let ssaoReDefine = false;
    let blurReDefine = false;
    let blurReSampler = false;

    if (options.epsilon !== undefined && this.options.epsilon !== options.epsilon) {
      ssaoReDefine = true;
    }

    if (options.samples !== undefined && this.options.samples !== options.samples) {
      ssaoReDefine = true;
      this.sampleSphere = this.generateHemisphere();
    }

    if (options.bypassBlur !== undefined && this.options.bypassBlur !== options.bypassBlur) {
      blurReDefine = true;
      blurReSampler = true;
    }

    if (options.expensiveBlur !== undefined && this.options.expensiveBlur !== options.expensiveBlur) {
      blurReDefine = true;
    }

    this.options = { ...this.options, ...options };

    if (ssaoReDefine) {
      this.ssaoPostProcess.updateEffect(this.getDefinesForSSAO());
    }

    if (blurReDefine || blurReSampler) {
      const defines = this.getDefinesForBlur(this.options.expensiveBlur, this.options.bypassBlur);
      if (blurReSampler) {
        const samplers = this.getSamplersForBlur(this.options.bypassBlur);
        this.blurHPostProcess.updateEffect(defines.h, null, samplers);
        this.blurVPostProcess.updateEffect(defines.v, null, samplers);
      } else {
        this.blurHPostProcess.updateEffect(defines.h);
        this.blurVPostProcess.updateEffect(defines.v);
      }
    }
  }

  // --------------------------------
  // SSAO Postprocess
  // --------------------------------
  private getDefinesForSSAO() {
    const defines = `#define SSAO\n#define SAMPLES ${
      this.options.samples
    }\n#define EPSILON ${this.options.epsilon.toFixed(4)}`;

    return defines;
  }
  private createSSAOPostProcess(ratio: number, textureType: number) {
    this.sampleSphere = this.generateHemisphere();

    const defines = this.getDefinesForSSAO();
    const samplers = ['randomSampler', 'depthSampler', 'normalSampler'];

    const ssaoPostProcess = new PostProcess(
      'ssao',
      'ssao',
      [
        'sampleSphere',
        'samplesFactor',
        'randTextureTiles',
        'totalStrength',
        'radius',
        'base',
        'range',
        'projection',
        'near',
        'texelSize',
        'xViewport',
        'yViewport',
        'maxZ',
        'minZAspect',
        'depthProjection',
      ],
      samplers,
      ratio,
      null,
      Texture.BILINEAR_SAMPLINGMODE,
      this.scene.getEngine(),
      false,
      defines,
      textureType
    );

    ssaoPostProcess.onApply = (effect: Effect) => {
      const camera = this.scene.activeCamera;
      if (!camera) {
        return;
      }

      const engine = this.scene.getEngine();

      effect.setArray3('sampleSphere', this.sampleSphere);
      effect.setFloat('randTextureTiles', 32.0);
      effect.setFloat('samplesFactor', 1 / this.options.samples);
      effect.setFloat('totalStrength', this.options.totalStrength);
      effect.setFloat2('texelSize', 1 / ssaoPostProcess.width, 1 / ssaoPostProcess.height);
      effect.setFloat('radius', this.options.radius);
      effect.setFloat('maxZ', this.options.maxZ);
      effect.setFloat('minZAspect', this.options.minZAspect);
      effect.setFloat('base', this.options.base);
      effect.setFloat('near', camera.minZ);
      if (camera.mode === Camera.PERSPECTIVE_CAMERA) {
        effect.setMatrix3x3('depthProjection', SSAOEffect.PERSPECTIVE_DEPTH_PROJECTION);
        effect.setFloat('xViewport', Math.tan(camera.fov / 2) * engine.getAspectRatio(camera, true));
        effect.setFloat('yViewport', Math.tan(camera.fov / 2));
      } else {
        const halfWidth = engine.getRenderWidth() / 2.0;
        const halfHeight = engine.getRenderHeight() / 2.0;
        const orthoLeft = camera.orthoLeft ?? -halfWidth;
        const orthoRight = camera.orthoRight ?? halfWidth;
        const orthoBottom = camera.orthoBottom ?? -halfHeight;
        const orthoTop = camera.orthoTop ?? halfHeight;
        effect.setMatrix3x3('depthProjection', SSAOEffect.ORTHO_DEPTH_PROJECTION);
        effect.setFloat('xViewport', (orthoRight - orthoLeft) * 0.5);
        effect.setFloat('yViewport', (orthoTop - orthoBottom) * 0.5);
      }
      effect.setMatrix('projection', this.scene.getProjectionMatrix());

      if (this.geometryBufferRenderer) {
        const gBuffer = this.geometryBufferRenderer.getGBuffer();
        if (gBuffer.textures[0]) {
          effect.setTexture('depthSampler', gBuffer.textures[0]);
        }
        if (gBuffer.textures[1]) {
          effect.setTexture('normalSampler', gBuffer.textures[1]);
        }
      } else if (this.prePassRenderer) {
        const renderTarget = this.prePassRenderer.getRenderTarget();
        const iDepth = this.prePassRenderer.getIndex(Constants.PREPASS_DEPTH_TEXTURE_TYPE);
        const iNormal = this.prePassRenderer.getIndex(Constants.PREPASS_NORMAL_TEXTURE_TYPE);
        if (renderTarget.textures[iDepth]) {
          effect.setTexture('depthSampler', renderTarget.textures[iDepth]);
        }
        if (renderTarget.textures[iNormal]) {
          effect.setTexture('normalSampler', renderTarget.textures[iNormal]);
        }
      }
      effect.setTexture('randomSampler', this.randomTexture);
    };
    if (!this.forceGeometryBuffer) {
      this.ssaoPostProcess._prePassEffectConfiguration = new SSAOConfiguration();
    }
    return ssaoPostProcess;
  }

  // --------------------------------
  // Blur Postprocess
  // --------------------------------
  private getSamplersForBlur(disabled: boolean): Array<string> {
    return disabled ? ['textureSampler'] : ['textureSampler', 'depthSampler'];
  }
  private getDefinesForBlur(bilateral: boolean, disabled: boolean): { h: string; v: string } {
    let define = '#define BLUR\n';
    if (disabled) {
      define += '#define BLUR_BYPASS\n';
    }
    if (!bilateral) {
      define += '#define BLUR_LEGACY\n';
    }
    return { h: define + '#define BLUR_H\n', v: define };
  }
  private createBlurPostProcess(ssaoRatio: number, blurRatio: number, textureType: number) {
    const defines = this.getDefinesForBlur(this.options.expensiveBlur, this.options.bypassBlur);
    const samplers = this.getSamplersForBlur(this.options.bypassBlur);

    return {
      H: this.createBlurFilter('BlurH', samplers, ssaoRatio, defines.h, textureType, true),
      V: this.createBlurFilter('BlurV', samplers, blurRatio, defines.v, textureType, false),
    };
  }
  private createBlurFilter(
    name: string,
    samplers: Array<string>,
    ratio: number,
    defines: string,
    textureType: number,
    horizontal: boolean
  ): PostProcess {
    const blurFilter = new PostProcess(
      name,
      'ssao',
      ['outSize', 'samples', 'soften', 'tolerance'],
      samplers,
      ratio,
      null,
      Texture.BILINEAR_SAMPLINGMODE,
      this.scene.getEngine(),
      false,
      defines,
      textureType
    );

    blurFilter.onApply = (effect: Effect) => {
      if (!this.scene.activeCamera) {
        return;
      }

      const ssaoCombineSize = horizontal ? this.ssaoCombinePostProcess.width : this.ssaoCombinePostProcess.height;
      const originalColorSize = horizontal ? this.originalColorPostProcess.width : this.originalColorPostProcess.height;

      effect.setFloat('outSize', ssaoCombineSize > 0 ? ssaoCombineSize : originalColorSize);
      effect.setInt('samples', this.options.bilateralSamples);
      effect.setFloat('soften', this.options.bilateralSoften);
      effect.setFloat('tolerance', this.options.bilateralTolerance);
      if (this.geometryBufferRenderer) {
        const gBuffer = this.geometryBufferRenderer.getGBuffer();
        if (gBuffer.textures[0]) {
          effect.setTexture('depthSampler', gBuffer.textures[0]);
        }
      } else if (this.prePassRenderer) {
        const renderTarget = this.prePassRenderer.getRenderTarget();
        const iDepth = this.prePassRenderer.getIndex(Constants.PREPASS_DEPTH_TEXTURE_TYPE);
        if (renderTarget.textures[iDepth]) {
          effect.setTexture('depthSampler', renderTarget.textures[iDepth]);
        }
      }
    };

    // blurFilter.samples = this.textureSamples;
    return blurFilter;
  }

  // --------------------------------
  // OriginalColor & Combine Postprocess
  // --------------------------------
  private createOriginalColorPostProcess(textureType: number) {
    const originalColorPostProcess = new PassPostProcess(
      'SSAOOriginalSceneColor',
      1.0,
      null,
      Texture.BILINEAR_SAMPLINGMODE,
      this.scene.getEngine(),
      undefined,
      textureType
    );
    return originalColorPostProcess;
  }
  private createSSAOCombinePostProcess(textureType: number) {
    const _ssaoCombinePostProcess = new PostProcess(
      'ssaoCombine',
      'ssaoCombine',
      [],
      ['originalColor', 'viewport'],
      1.0,
      null,
      Texture.BILINEAR_SAMPLINGMODE,
      this.scene.getEngine(),
      false,
      undefined,
      textureType
    );

    _ssaoCombinePostProcess.onApply = (effect: Effect) => {
      const viewport = this.scene.activeCamera!.viewport;
      effect.setVector4(
        'viewport',
        TmpVectors.Vector4[0].copyFromFloats(viewport.x, viewport.y, viewport.width, viewport.height)
      );
      effect.setTextureFromPostProcessOutput('originalColor', this.originalColorPostProcess);
    };

    return _ssaoCombinePostProcess;
  }

  // --------------------------------
  // Utiles & Calculator
  // --------------------------------
  private createRandomTexture(): void {
    const size = 128;

    this.randomTexture = new DynamicTexture(
      'SSAORandomTexture',
      size,
      this.scene,
      false,
      Texture.BILINEAR_SAMPLINGMODE
    );
    this.randomTexture.wrapU = Texture.WRAP_ADDRESSMODE;
    this.randomTexture.wrapV = Texture.WRAP_ADDRESSMODE;

    const context = this.randomTexture.getContext();

    const rand = (min: number, max: number) => {
      return Math.random() * (max - min) + min;
    };

    const randVector = Vector3.Zero();

    for (let x = 0; x < size; x++) {
      for (let y = 0; y < size; y++) {
        randVector.x = rand(0.0, 1.0);
        randVector.y = rand(0.0, 1.0);
        randVector.z = 0.0;

        randVector.normalize();

        randVector.scaleInPlace(255);
        randVector.x = Math.floor(randVector.x);
        randVector.y = Math.floor(randVector.y);

        context.fillStyle = 'rgb(' + randVector.x + ', ' + randVector.y + ', ' + randVector.z + ')';
        context.fillRect(x, y, 1, 1);
      }
    }

    this.randomTexture.update(false);
  }
  private generateHemisphere(): number[] {
    const numSamples = this.options.samples;
    const result = [];
    let vector;

    let i = 0;
    while (i < numSamples) {
      if (numSamples < 16) {
        vector = this.hemisphereSample_uniform(Math.random(), Math.random());
      } else {
        const rand = this.hammersley(i, numSamples);
        vector = this.hemisphereSample_uniform(rand[0], rand[1]);
      }

      result.push(vector.x, vector.y, vector.z);
      i++;
    }

    return result;
  }

  private _bits = new Uint32Array(1);
  private radicalInverse_VdC(i: number) {
    this._bits[0] = i;
    this._bits[0] = ((this._bits[0] << 16) | (this._bits[0] >> 16)) >>> 0;
    this._bits[0] = ((this._bits[0] & 0x55555555) << 1) | (((this._bits[0] & 0xaaaaaaaa) >>> 1) >>> 0);
    this._bits[0] = ((this._bits[0] & 0x33333333) << 2) | (((this._bits[0] & 0xcccccccc) >>> 2) >>> 0);
    this._bits[0] = ((this._bits[0] & 0x0f0f0f0f) << 4) | (((this._bits[0] & 0xf0f0f0f0) >>> 4) >>> 0);
    this._bits[0] = ((this._bits[0] & 0x00ff00ff) << 8) | (((this._bits[0] & 0xff00ff00) >>> 8) >>> 0);
    return this._bits[0] * 2.3283064365386963e-10; // / 0x100000000 or / 4294967296
  }
  private hammersley(i: number, n: number): [number, number] {
    return [i / n, this.radicalInverse_VdC(i)];
  }
  private hemisphereSample_uniform(u: number, v: number): Vector3 {
    const phi = v * 2.0 * Math.PI;
    // rejecting samples that are close to tangent plane to avoid z-fighting artifacts
    const cosTheta = 1.0 - u * 0.85;
    const sinTheta = Math.sqrt(1.0 - cosTheta * cosTheta);
    return new Vector3(Math.cos(phi) * sinTheta, Math.sin(phi) * sinTheta, cosTheta);
  }
}

export class SSAOConfiguration implements PrePassEffectConfiguration {
  /**
   * Is subsurface enabled
   */
  public enabled = false;

  /**
   * Name of the configuration
   */
  public name = 'ssao';

  /**
   * Textures that should be present in the MRT for this effect to work
   */
  public readonly texturesRequired: number[] = [
    Constants.PREPASS_NORMAL_TEXTURE_TYPE,
    Constants.PREPASS_DEPTH_TEXTURE_TYPE,
  ];
}
